import React, { useMemo } from 'react';
import { Row } from 'react-grid-system';
import { useDispatch, useSelector } from 'react-redux';
import Card from '@Components/Card/Card';
import { PrimaryMediumButtonFixed } from '@Components/controls/Button/Button';
import ConditionalRender from '@Components/ConditionalRender/ConditionalRender';
import { setOutletPickupFocus } from '@Redux/modules/addressBook/actions';
import { IAddress } from '@ApiModels/commerceProfile';
import { ICartItem, IInsuranceCartItem } from '@ApiModels/viewCart';
import { DISABLE_PICKUP_FROM_OUTLET } from '@Config/features.config';
import useTranslate from '@Utils/hooks/useTranslate';
import { EMPTY_OUTLET, useOutlet } from '@Utils/context/OutletContext';
import { useBuyNowPayLater } from '@Utils/context/BuyNowPayLaterContext';
import PageContent from './PageContent';
import FilterAddresses from './FilterAddresses';
import { SelectedAddressType } from '../ShippingMethod';
import AddressBookAvailable from './AddressBookAvailable';
import AddressBookNotAvailable from './AddressBookNotAvailable';
import InternetInstallation from '../../ShippingDetails/components/InternetInstallation';
import { RadioButtonWrapper, PickupSelectorContainer } from './../ShippingMethod.styled';

interface INotHBBWFBB {
	addNewAddress: () => void;
	addressBook: IAddress[];
	addressType: SelectedAddressType;
	hasDeviceWithHbbWfbbItem: boolean | undefined;
	hbbWfbbItem: ICartItem | IInsuranceCartItem | undefined;
	isPrepaidPurchase: boolean | undefined;
	launchDate: string | undefined;
	onlyPickupItems: boolean;
	orderIncludesPlan: boolean;
	setAddressType: (type: SelectedAddressType) => void;
}

const NotHBBWFBB = ({
	addNewAddress,
	addressBook,
	addressType,
	hasDeviceWithHbbWfbbItem,
	hbbWfbbItem,
	isPrepaidPurchase,
	launchDate,
	onlyPickupItems,
	orderIncludesPlan,
	setAddressType,
}: INotHBBWFBB): JSX.Element => {
	const { translate } = useTranslate();
	const dispatch = useDispatch();
	const newAddressBook = useSelector((state: types.redux.IState) => state.addressBook.newAddressBook);
	const { availableOutlets, setSelectedOutlet, setAlertVisibility } = useOutlet();
	const { isBuyNowPayLaterInCart } = useBuyNowPayLater();

	const EnterAddressBtn = () => (
		<Row>
			<PrimaryMediumButtonFixed
				uniqueId="shipping-method.enter-address"
				onClick={() => {
					addNewAddress();
				}}
			>
				{translate('shipping-method.enter-address')}
			</PrimaryMediumButtonFixed>
		</Row>
	);

	const differentAddress = () => {
		if (newAddressBook) {
			return (
				<RadioButtonWrapper>
					<FilterAddresses
						addressBook={addressBook}
						addressType={addressType}
						filter={newAddressBook}
						isPrepaidPurchase={isPrepaidPurchase}
					/>
				</RadioButtonWrapper>
			);
		} else {
			return EnterAddressBtn();
		}
	};

	const pickupSelector = useMemo(
		() =>
			!isPrepaidPurchase ? (
				<PickupSelectorContainer>
					<Card<types.cards.IDeliveryMethodCardProps>
						card="deliveryMethod"
						params={{
							title: translate('shipping-method.deliver.address.title'),
							subtitle: translate('shipping-method.deliver.address.subtitle'),
							icon: 'location',
							selected:
								addressType === SelectedAddressType.DIFFERENT ||
								addressType === SelectedAddressType.ADRESS_BOOK,
						}}
						onEvent={() => {
							setAddressType(SelectedAddressType.DIFFERENT);
							setSelectedOutlet(EMPTY_OUTLET);
							dispatch(setOutletPickupFocus({ outletPickupFocus: false }));
							setAlertVisibility(false);
						}}
						withoutContainer
					/>
					{/* remove comment when pickup will be available */}
					{/* <Card<types.cards.IDeliveryMethodCardProps>
					card="deliveryMethod"
					params={{
						title: translate('shipping-method.deliver.pickup.title'),
						subtitle: translate('shipping-method.deliver.pickup.subtitle'),
						icon: 'locationBuild',
						selected: addressType === SelectedAddressType.PICKUP,
						disabled: !onlyPickupItems,
					}}
					onEvent={() => {
						setAddressType(SelectedAddressType.PICKUP);
					}}
					withoutContainer
				/>
				<ConditionalRender
					show={!onlyPickupItems}
					onTrue={() => <PickupOrderInfo>{translate('shipping-method.pickup.info')}</PickupOrderInfo>}
				/> */}
					<ConditionalRender
						show={!DISABLE_PICKUP_FROM_OUTLET}
						onTrue={() => (
							<Card<types.cards.IDeliveryMethodCardProps>
								card="deliveryMethod"
								params={{
									title: translate('shipping-method.deliver.outlet.title'),
									subtitle: translate('shipping-method.deliver.outlet-pickup.subtitle'),
									icon: 'locationBuild',
									selected: addressType === SelectedAddressType.OUTLET,
									disabled:
										availableOutlets.filter((item) => {
											return item.available === true;
										}).length === 0 || isBuyNowPayLaterInCart,
								}}
								onEvent={() => {
									setAddressType(SelectedAddressType.OUTLET);
									dispatch(setOutletPickupFocus({ outletPickupFocus: true }));
								}}
								withoutContainer
							/>
						)}
					/>
				</PickupSelectorContainer>
			) : (
				<></>
			),
		[onlyPickupItems, addressType]
	);

	const iconName = !!hasDeviceWithHbbWfbbItem ? 'deliveryBus' : 'shipping';
	const title = translate(
		hasDeviceWithHbbWfbbItem
			? 'shipping-method.title.hbb.wfbb.device'
			: orderIncludesPlan && !isPrepaidPurchase
			? 'shipping-method.title'
			: 'shipping-method.title.without.plan'
	);
	const description = translate(
		hasDeviceWithHbbWfbbItem
			? 'shipping-method.title.hbb.wfbb.desc.device'
			: isPrepaidPurchase
			? 'shipping-method.description-sim'
			: 'shipping-method.description'
	);
	const addressBookAvailable =
		addressBook?.length > 0 && (addressBook[0].wilayat !== '' || addressBook[0].way !== '');

	return (
		<PageContent title={title} description={description} name={iconName}>
			<>
				<ConditionalRender
					show={!!hasDeviceWithHbbWfbbItem}
					onTrue={() => <InternetInstallation addressType={addressType} setAddressType={setAddressType} />}
				/>
				<ConditionalRender
					show={addressBookAvailable}
					onTrue={() => (
						<AddressBookAvailable
							addressType={addressType}
							differentAddress={differentAddress}
							hbbWfbbItem={hbbWfbbItem}
							hasDeviceWithHbbWfbbItem={hasDeviceWithHbbWfbbItem}
							isPrepaidPurchase={isPrepaidPurchase}
							launchDate={launchDate}
							pickupSelector={pickupSelector}
							setAddressType={setAddressType}
						/>
					)}
					onFalse={() => (
						<AddressBookNotAvailable
							addressType={addressType}
							differentAddress={differentAddress}
							isPrepaidPurchase={isPrepaidPurchase}
							pickupSelector={pickupSelector}
							setAddressType={setAddressType}
						/>
					)}
				/>
			</>
		</PageContent>
	);
};

export default NotHBBWFBB;
